import { useState } from 'react'
import { MutationUpdaterFn, Reference } from '@apollo/client'
import { Redirect } from 'react-router-dom'

import { DeleteTemplateMutation, useDeleteTemplateMutation } from '../../../../generated/graphql'
import { ConfirmDeleteTemplateModal } from '../../templateModals'
import useModal from '../../../../lib/hooks/useModal'
import useNotification from '../../../../lib/hooks/useNotification'
import { markNavigationIntentional } from '../../../../lib/navigationGuard'

type ConfirmDeleteTemplateModalWrapperProps = {
  /** ID of Plan */
  id: string
  /** Determines visibility of modal */
  isOpen: boolean
  /** Current template page settings used for redirecting after delete */
  search: string
}

const ConfirmDeleteTemplateModalWrapper = ({
  id,
  isOpen,
  search
}: ConfirmDeleteTemplateModalWrapperProps) => {
  const { hideModal } = useModal()
  const [deleteCompleted, setDeleteCompleted] = useState(false)
  const { showErrorNotif } = useNotification()

  const update: MutationUpdaterFn<DeleteTemplateMutation> = (cache, { data }) => {
    cache.modify({
      id: 'ROOT_QUERY',
      fields: {
        templates(templates: Reference[], { readField }) {
          return templates.filter(
            (template) =>
              // remove deleted template
              readField('id', template) !== data?.deleteTemplate.id
          )
        }
      }
    })
  }

  const onCompleted = (data: DeleteTemplateMutation): void => {
    if (data && data.deleteTemplate) {
      // The Redirect below triggers a sibling history.block guard
      // (TemplateBuilder's leave-draft prompt) — flag the nav so it lets us through.
      markNavigationIntentional()
      setDeleteCompleted(true)
    }
    hideModal()
  }

  const [deleteTemplate, mutation] = useDeleteTemplateMutation({
    variables: { id },
    update,
    onCompleted,
    onError: () => {
      hideModal()
      showErrorNotif('Template could not be deleted.', deleteTemplate)
    },
    optimisticResponse: {
      __typename: 'Mutation',
      deleteTemplate: {
        __typename: 'ResponseStatus',
        id,
        message: '',
        success: true
      }
    }
  })

  const onConfirm = () => {
    deleteTemplate()
  }

  if (deleteCompleted) {
    return <Redirect to={`/templates/plan${search}`} />
  }

  if (!mutation.called) {
    return <ConfirmDeleteTemplateModal isOpen={isOpen} onConfirm={onConfirm} label="Template" />
  }

  return null
}

export default ConfirmDeleteTemplateModalWrapper
